import { Inject, Injectable } from '@angular/core';
import {
    JsonMaterialQuoteModel,
    LocalStorageCommonService,
    MaterialQuoteModel,
    MaterialQuoteModelFromJson,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationIdentifier,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageSalesQuoteService {
    private readonly KEY_SALES_QUOTE = 'salesQuote';
    public constructor(
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public async saveSalesQuoteAsync(salesQuote: MaterialQuoteModel): Promise<void> {
        if (!salesQuote.notificationModel) {
            throw new Error('Tried to save MaterialQuoteModel without notification!');
        }

        await this.filterSalesQuoteByNotificationIdentifierAsync(salesQuote.notificationModel.identifier)
            .then((filteredSalesQuote) => {
                filteredSalesQuote.push(salesQuote);
                return this.localStorageCommonService.storageSetAsync(this.KEY_SALES_QUOTE, filteredSalesQuote);
            })
            .catch((error) => {
                this.log.error(this.constructor.name, 'failed to save a sales quote', error);
            });
    }

    public async getSalesQuoteAsync(
        notificationIdentifier: NotificationIdentifierModel,
    ): Promise<MaterialQuoteModelFromJson | undefined> {
        return this.localStorageCommonService
            .storageGetAsync<JsonMaterialQuoteModel[]>(this.KEY_SALES_QUOTE)
            .then((allSalesQuotes) => {
                if (!allSalesQuotes?.length) {
                    return undefined;
                }

                const jsonSalesQuote = allSalesQuotes.find((localSalesQuote) => {
                    if (!localSalesQuote.notificationIdentifier) {
                        return false;
                    }

                    return new NotificationIdentifierModelFromJson(localSalesQuote.notificationIdentifier).equalsTo(
                        notificationIdentifier,
                    );
                });

                if (jsonSalesQuote) {
                    return new MaterialQuoteModelFromJson(jsonSalesQuote);
                }

                return undefined;
            });
    }

    public async removeSalesQuoteFromLocalStorageAsync(notificationIdentifier: NotificationIdentifier): Promise<void> {
        return this.filterSalesQuoteByNotificationIdentifierAsync(notificationIdentifier)
            .then((filteredSalesQuote) =>
                this.localStorageCommonService.storageSetAsync(this.KEY_SALES_QUOTE, filteredSalesQuote),
            )
            .catch((error) => {
                this.log.error(this.constructor.name, 'failed to remove salesQuote from local storage', error);
            });
    }

    /**
     * This function will return all the stored sales quote data
     * excluding the sales quote for the given notification identifier.
     * It will also exclude any sales quote data stored for notifications no longer present in current NDP.
     */

    private async filterSalesQuoteByNotificationIdentifierAsync(
        notificationIdentifier: NotificationIdentifier,
    ): Promise<MaterialQuoteModel[]> {
        return this.localStorageCommonService
            .storageGetAsync<JsonMaterialQuoteModel[]>(this.KEY_SALES_QUOTE)
            .then((jsonSalesQuotesArray) => {
                if (!jsonSalesQuotesArray?.length) {
                    return [];
                }
                const notificationsArray = this.notificationService.getAllNotifications();
                return jsonSalesQuotesArray
                    .filter((jsonSalesQuote) => {
                        if (!jsonSalesQuote.notificationIdentifier) {
                            return false;
                        }

                        const localNotificationIdentifier = new NotificationIdentifierModelFromJson(
                            jsonSalesQuote.notificationIdentifier,
                        );
                        const isNotificationValid =
                            notificationsArray.findIndex((notification) =>
                                notification.identifier.equalsTo(localNotificationIdentifier),
                            ) > -1;

                        return (
                            isNotificationValid &&
                            !NotificationIdentifierModel.compare(notificationIdentifier, localNotificationIdentifier)
                        );
                    })
                    .map((jsonSalesQuote) => new MaterialQuoteModelFromJson(jsonSalesQuote));
            });
    }
}
